import { useRecoilState, useRecoilValue } from 'recoil'
import { useCallback, useRef } from 'react'
import {
  selectedBranchAtom,
  branchesAtom,
  exchangeRatesAtom,
  isLoadingRatesAtom,
  ratesErrorAtom,
  ratesLastUpdatedAtom,
} from '../state/atoms'
import { getExchangeRates } from '../api/services'
import { ApiError } from '../api/client'
import type { ExchangeRate } from '../api/types'

// BroadcastChannel for customer display
const CHANNEL_NAME = 'pos-state'

export function useExchange() {
  const branches = useRecoilValue(branchesAtom)
  const [selectedBranch, setSelectedBranch] = useRecoilState(selectedBranchAtom)
  const [exchangeRates, setExchangeRates] = useRecoilState(exchangeRatesAtom)
  const [isLoadingRates, setIsLoadingRates] = useRecoilState(isLoadingRatesAtom)
  const [ratesError, setRatesError] = useRecoilState(ratesErrorAtom)
  const [ratesLastUpdated, setRatesLastUpdated] = useRecoilState(ratesLastUpdatedAtom)
  const channelRef = useRef<BroadcastChannel | null>(null)

  // Get or create broadcast channel
  const getChannel = useCallback(() => {
    if (!channelRef.current) {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME)
    }
    return channelRef.current
  }, [])

  // Send rates to customer display
  const broadcastRates = useCallback((rates: Map<string, ExchangeRate>) => {
    try {
      const channel = getChannel()
      const ratesObj: Record<string, { buy: number; sell: number; currency: string }> = {}

      rates.forEach((rate, currency) => {
        ratesObj[currency] = {
          buy: parseFloat(rate.bidRate),
          sell: parseFloat(rate.askRate),
          currency: rate.currencyCode,
        }
      })

      channel.postMessage({
        type: 'EXCHANGE_RATE_UPDATE',
        payload: { rates: ratesObj, branchName: selectedBranch?.name },
        senderId: 'staff-pos',
        timestamp: Date.now(),
      })
      console.log('📡 Broadcasted rates to customer display')
    } catch (e) {
      console.warn('Failed to broadcast rates:', e)
    }
  }, [getChannel, selectedBranch])

  // ============================================
  // Load Exchange Rates
  // ============================================
  const loadExchangeRates = useCallback(
    async (branchNid: string) => {
      setIsLoadingRates(true)
      setRatesError(null)

      try {
        console.log('loadExchangeRates', branchNid)
        const response = await getExchangeRates(branchNid)
        const ratesMap = new Map<string, ExchangeRate>()

        response.exchangeRates.forEach((rate) => {
          ratesMap.set(rate.currencyCode, rate)
        })

        console.log(`✓ Loaded ${response.exchangeRates.length} exchange rates`)
        setExchangeRates(ratesMap)
        setRatesLastUpdated(new Date())

        // Broadcast to customer display
        broadcastRates(ratesMap)
      } catch (error) {
        console.error('Failed to load exchange rates:', error)
        if (error instanceof ApiError) {
          setRatesError(`환율 로드 실패: ${error.message}`)
        } else {
          setRatesError('환율 정보를 불러올 수 없습니다.')
        }
      } finally {
        setIsLoadingRates(false)
      }
    },
    [setExchangeRates, setIsLoadingRates, setRatesError, setRatesLastUpdated, broadcastRates]
  )

  // ============================================
  // Get Rate Helper
  // ============================================
  const getRate = useCallback(
    (currency: string, type: 'buy' | 'sell'): number => {
      const rate = exchangeRates.get(currency)
      if (!rate) return 0

      const rateValue = type === 'buy' ? rate.bidRate : rate.askRate
      return parseFloat(rateValue)
    },
    [exchangeRates]
  )

  // ============================================
  // Refresh Rates
  // ============================================
  const refreshRates = useCallback(async () => {
    if (selectedBranch) {
      await loadExchangeRates(selectedBranch.nid)
    }
  }, [selectedBranch, loadExchangeRates])

  return {
    branches,
    selectedBranch,
    setSelectedBranch,
    exchangeRates,
    isLoadingRates,
    ratesError,
    ratesLastUpdated,
    getRate,
    refreshRates,
    loadExchangeRates,
  }
}
